import { UserRole } from "../authApi";

export enum AccessType {
  Paying = "PAYING",
  Evaluation = "EVALUATION",
  Internal = "INTERNAL",
  Demo = "DEMO",
  CustomAgreement = "CUSTOM_AGREEMENT",
}

export enum CustomerType {
  ApiOnly = "API ONLY",
  Distributor = "DISTRIBUTOR",
  DistributorsCustomer = "DISTRIBUTOR'S CUSTOMER",
  RegularCustomer = "REGULAR CUSTOMER",
}

export enum ProductType {
  PortalOnly = "PORTAL_ONLY",
  PortalWithApi = "PORTAL_WITH_API",
}

export enum UserPricing {
  NetworkPackages = "NETWORK_PACKAGES",
  OldNetworkModel = "OLD_NETWORK_MODEL",
}

export enum UserType {
  ApiOnly = "API_ONLY",
  Portal = "PORTAL",
}

export interface LimitSchema {
  customizable: boolean;
  description: string;
  displayOrder: number;
  label: string;
  name: string;
  productId?: string;
  requiredRole: UserRole;
  type: LimitType;
  unitLabel: string;
  visible: boolean;
}

export interface LimitsSharedWithProduct {
  productId: string;
  names: string[];
}

export enum LimitType {
  Boolean = "BOOLEAN",
  Count = "COUNT",
  Date = "DATE",
  DateRange = "DATE_RANGE",
  Km = "KM",
  Geo = "GEO",
  SquareKm = "SQUARE_KM",
}

export interface LimitUsages {
  [limitName: string]: unknown;
}

export interface LimitValues {
  [limitName: string]: unknown;
}

export type EmailNotificationsSettings = {
  application: boolean;
  releaseNotes: boolean;
};

export interface MeDetailsDto {
  activated: boolean;
  archived: boolean;
  customerType: CustomerType;
  email: string;
  emailNotifications: Record<string, EmailNotificationsSettings>;
  locked: boolean;
  notificationsEnabled: boolean;
  organization: UserOrganization;
  pricing: UserPricing;
  products: UserProduct[];
  role: UserRole;
  settings: UserSettings;
  type: UserType;
}

export interface MeDetails extends MeDetailsDto {
  isFallback?: never;
}

export interface MeDetailsFallback
  extends Pick<MeDetailsDto, "email" | "role" | "settings"> {
  isFallback: true;
  products: UserProductFallback[];
}

export type MeDetailsSafe = (MeDetails | MeDetailsFallback) & {
  products: UserProductSafe[];
};

export function isMeDetails(meDetails: MeDetailsSafe): meDetails is MeDetails {
  return !("isFallback" in meDetails);
}

export function isMeDetailsFallback(
  meDetails: MeDetailsSafe
): meDetails is MeDetailsFallback {
  return meDetails.isFallback === true;
}

export interface MeProductAccess {
  type: AccessType;
  expiryDate: string;
  grantedAt: string;
  limits: LimitValues;
  limitsSharedWith: LimitsSharedWithProduct[];
  usage: LimitUsages;
}

export interface ProductDto {
  displayName: string;
  id: string;
  informationLink: string;
  name: string;
  productType: ProductType;
  redirectLink: string;
}

export interface Product extends ProductDto {}

export type ProductAccess =
  | {
      status: "DENIED";
    }
  | {
      status: "EXPIRED";
      accessType: AccessType;
      apiKey: string | null;
      expiryDate: string;
    }
  | {
      status: "GRANTED";
      accessType: AccessType;
      apiKey: string | null;
      expiryDate: string;
      grantedBy: string;
    }
  | {
      status: "PENDING";
      accessType: AccessType;
      expiryDate: string;
    }
  | {
      status: "REVOKED";
      accessType: AccessType;
      apiKey: string | null;
      expiryDate: string;
      grantedBy: string;
    };

export type ProductAccessFallback =
  | {
      status: "EXPIRED";
      apiKey: string | null;
      expiryDate: string;
    }
  | {
      status: "GRANTED";
      apiKey: string | null;
      expiryDate: string;
    };

export type ProductAccessSafe = ProductAccess | ProductAccessFallback;

export interface ProductFallback
  extends Pick<Product, "id" | "displayName" | "redirectLink"> {}

export type ProductResell =
  | { status: "DENIED" }
  | { status: "ALLOWED"; expiryDate: string; grantedBy: string };

export type ReleaseNotesItem = {
  id: number;
  date: string;
  message: string;
};

export interface UserOrganization {
  id: number;
  name: string;
  sapId: string;
}

export interface UserProduct extends Omit<Product, "redirectLink"> {
  isFallback?: never;
  access: ProductAccess;
  resell: ProductResell;
  url: string;
}

export interface UserProductFallback {
  isFallback: true;
  access: ProductAccessFallback;
  displayName: string;
  id: string;
  url: string;
}

export type UserProductSafe = (UserProduct | UserProductFallback) & {
  access: ProductAccessSafe;
};

export function isUserProduct(
  product: UserProductSafe
): product is UserProduct {
  return !("isFallback" in product);
}

export function isUserProductFallback(
  product: UserProductSafe
): product is UserProductFallback {
  return product.isFallback === true;
}

export interface UserSettings {
  clockFormat: string;
  dateFormat: string;
  weekStart: "MONDAY" | "SUNDAY";
  units: "KM" | "MI";
}
